import { useCallback, useEffect, useState } from "react";

export type ListQueryState<T> =
  | { status: "loading" }
  | { status: "error"; message: string; error: unknown }
  | { status: "success"; data: T };

/**
 * Fetches async data and tracks loading/error/success states with retry.
 * Callers must handle the error state explicitly — errors are never silently swallowed.
 *
 * @param fetcher - async function that returns data; recreated when deps change. It is told whether it is still the
 *   current request (`isCurrent`), so a fetcher that asks more than once can stop when the query has moved on.
 * @param deps - dependency array that triggers a new fetch when any value changes
 */
export function useListQuery<T>(
  fetcher: (request: { isCurrent: () => boolean }) => Promise<T>,
  deps: unknown[],
): { state: ListQueryState<T>; retry: () => void } {
  const [state, setState] = useState<ListQueryState<T>>({ status: "loading" });
  const [retryVersion, setRetryVersion] = useState(0);

  const retry = useCallback(() => setRetryVersion((v) => v + 1), []);

  useEffect(() => {
    let cancelled = false;
    setState({ status: "loading" });

    fetcher({ isCurrent: () => !cancelled })
      .then((data) => {
        if (!cancelled) setState({ status: "success", data });
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setState({
            status: "error",
            message: err instanceof Error ? err.message : String(err),
            error: err,
          });
        }
      });

    return () => {
      cancelled = true;
    };
    // deps and retryVersion are the intended trigger; fetcher is a closure over those deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, retryVersion]);

  return { state, retry };
}
